/**
 * Type definitions for the per-facility delivery-steps configuration document at
 * `api_config/{orgId}/delivery-steps/v1`. The document orchestrates the pharmacy
 * authorization delivery pipeline (track status, text notes, letter upload, etc.).
 *
 * Only the pieces consumed by the Track Status tab are modelled in detail; sibling
 * sections are kept as opaque records so partial writes never drop them.
 */

/** Default stale window (minutes) when a `stale_in` condition omits `stale_after_minutes`. */
export const DEFAULT_STALE_AFTER_MINUTES = 30;

/**
 * A single delivery-step condition. `values` is used by multi-value operators
 * (`in`, `not_in`, `stale_in`); `value` by scalar operators; empty operators
 * (`is_empty`, `is_not_empty`) carry neither.
 */
export interface DeliveryRunWhenCondition {
  id?: string;
  field?: string;
  operator: string;
  values?: (string | number)[];
  value?: string | number;
  stale_after_minutes?: number;
  /** Used by the `all_fields_present` operator (Stats Storage required fields). */
  fields?: string[];
}

/** A nested AND/OR group of delivery-step conditions. */
export interface DeliveryRunWhenGroup {
  logic: "AND" | "OR";
  conditions: DeliveryConditionNode[];
}

/** Either a leaf condition or a nested group. */
export type DeliveryConditionNode =
  DeliveryRunWhenCondition | DeliveryRunWhenGroup;

/** Narrows a condition node to a nested group (groups carry a `conditions` array). */
export function isDeliveryConditionGroup(
  node: DeliveryConditionNode,
): node is DeliveryRunWhenGroup {
  return Array.isArray((node as DeliveryRunWhenGroup).conditions);
}

/** Batch/retry tuning for a delivery step's RPA execution. */
export interface TrackStatusSettings {
  max_retries?: number;
  batch_size?: number;
  batch_stagger_seconds?: number;
  rpa_timeout_seconds?: number;
  distributed_batch_size?: number;
}

/** The `track_status` section of the delivery-steps document. */
export interface TrackStatusConfig {
  tab_label?: string;
  action?: string;
  status_field?: string;
  enabled?: boolean;
  settings?: TrackStatusSettings;
  run_when?: DeliveryRunWhenGroup;
}

/**
 * Title-rendering rule for one PA status bucket in a delivery step's generated
 * document name. `title_alias` renames the bucket; `include_drug_name` appends
 * the drug name to the document title.
 */
export interface PaStatusTitleOption {
  title_alias?: string | null;
  include_drug_name?: boolean;
}

/** One column rendered into the generated text-note body. */
export interface TextNoteBodyColumn {
  field_name: string;
  display_label: string | null;
  format_template: string;
  render_order: number;
  required: boolean;
  bq_columns: string[];
  /** SQL fallback select used when the column is absent (e.g. pa_case_id). */
  fallback_select?: string;
  /**
   * Label a routing rule's `what_we_add` must contain for this block to render.
   * When absent the RPA falls back to its built-in field-name map, and a column
   * missing from that map renders unconditionally.
   */
  what_we_add_label?: string | null;
  /** "runtime" for computed columns (summary, comment); preserved untouched. */
  source?: string | null;
}

/** HTML wrappers used when rendering the text-note body fields. */
export interface HtmlFormatting {
  field_separator: string;
  field_with_label: string;
  field_without_label: string;
  image_template: string;
}

/** Screenshot attachment config for the generated text note. */
export interface ScreenshotConfig {
  field_name: string;
  image_type: string;
  mime_type: string;
  render_order: number;
  required: boolean;
  storage_path_template: string;
}

/**
 * One summary-resolution rule. `value_template` produces the summary value when
 * every `when` clause matches (a clause is `column -> allowed values`), e.g. the
 * `calling_resolved` rule (call_required in [yes, archive], calling_status in [Resolved]).
 */
export interface SummaryRule {
  id: string;
  value_template: string;
  when: Record<string, string[]>;
}

/** Summary-field resolution config for the text note. */
export interface SummaryResolution {
  enabled: boolean;
  field_name: string;
  source_columns: string[];
  rules: SummaryRule[];
}

/**
 * The generated text-note body config. Strongly typed for the pieces the UI
 * edits; the index signature keeps any unknown keys round-tripping untouched.
 */
export interface TextNotesContent {
  text_note_body_columns?: TextNoteBodyColumn[];
  html_formatting?: HtmlFormatting;
  screenshot_config?: ScreenshotConfig;
  summary_resolution?: SummaryResolution;
  required_cmm_stats_fields?: string[];
  [key: string]: unknown;
}

/** Note state a routing rule produces, and whether it may later become signed. */
export interface TextNotesRoutingStatus {
  state?: string;
  can_transition_to_signed?: boolean;
}

/**
 * One routing rule: for orders on these `route_via` keys with these
 * `response_status` values, the generated note gets this heading, note state,
 * and set of sections (`what_we_add`). The index signature keeps keys the UI
 * doesn't edit (e.g. `response_status_members`) round-tripping untouched.
 */
export interface TextNotesRoutingRule {
  route_via?: string[] | null;
  response_status?: string[] | null;
  response_status_alias?: string | null;
  text_notes_heading?: string | null;
  text_notes_status?: TextNotesRoutingStatus;
  sign_required?: boolean | null;
  what_we_add?: string[] | null;
  [key: string]: unknown;
}

/**
 * Document-generation config for the Text Notes step. `content` (body columns,
 * screenshot, summary, html formatting, required fields) is edited via the Text
 * Notes tab alongside the title, status source, routing rules, and PA status buckets.
 */
export interface TextNotesDocumentConfig {
  content?: TextNotesContent;
  routing?: TextNotesRoutingRule[];
  pa_status_title_options?: Record<string, PaStatusTitleOption>;
  status_source_field?: string;
  status_field?: string;
  title_template?: string;
}

/** The `text_notes` section of the delivery-steps document. */
export interface TextNotesConfig {
  tab_label?: string;
  action?: string;
  status_field?: string;
  enabled?: boolean;
  settings?: TrackStatusSettings;
  document_config?: TextNotesDocumentConfig;
  preconditions?: DeliveryRunWhenGroup;
  run_when?: DeliveryRunWhenGroup;
}

/** Per-bucket generated letter for the Letter Upload step. */
export interface LetterUploadDocument {
  document_type?: string;
  filename_template?: string;
}

/**
 * Document-generation config for the Letter Upload step. `documents` maps a PA
 * status bucket to the letter it produces (type + filename template).
 */
export interface LetterUploadDocumentConfig {
  documents?: Record<string, LetterUploadDocument>;
  pa_status_title_options?: Record<string, PaStatusTitleOption>;
  status_source_field?: string;
  title_template?: string;
}

/** The `letter_upload` section of the delivery-steps document. */
export interface LetterUploadConfig {
  tab_label?: string;
  action?: string;
  status_field?: string;
  enabled?: boolean;
  settings?: TrackStatusSettings;
  document_config?: LetterUploadDocumentConfig;
  preconditions?: DeliveryRunWhenGroup;
  run_when?: DeliveryRunWhenGroup;
}

/**
 * The `stats_storage` section. Its `preconditions` carry the eligible response
 * statuses (`response_status in [...]`) and the required stats fields
 * (`all_fields_present`), which the UI surfaces as two editable bucket lists.
 */
export interface StatsStorageConfig {
  tab_label?: string;
  action?: string;
  status_field?: string;
  enabled?: boolean;
  status_aliases?: Record<string, string>;
  preconditions?: DeliveryRunWhenGroup;
}

/** Top-level concurrency / idempotency knobs shared by every delivery step. */
export interface DeliveryGlobalConfig {
  max_concurrent_identifiers?: number;
  idempotency_window_hours?: number;
  stale_threshold_minutes?: number;
}

/**
 * The full delivery-steps document. Sibling sections (text_notes, letter_upload,
 * stats_storage) are opaque so editing one section never overwrites the others.
 */
export interface DeliveryStepsDoc {
  id?: string;
  schema_version?: number;
  is_active?: boolean;
  config_name?: string;
  config_version?: string;
  global?: DeliveryGlobalConfig;
  track_status?: TrackStatusConfig;
  text_notes?: TextNotesConfig;
  letter_upload?: LetterUploadConfig;
  stats_storage?: StatsStorageConfig;
  [key: string]: unknown;
}
